import { useEffect, useState } from 'react';
import api from '../lib/api';
import { KpiCard } from '../components/dashboard/KpiCard';
import {
  DollarSign,
  Megaphone,
  Briefcase,
  Award,
  Percent,
  Calendar as CalIcon,
  Bell,
  CheckSquare,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from 'recharts';
import { fmtCurrency, fmtNumber, STATUS_LABELS, fmtDateTime } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { UpcomingFollowupsWidget } from '../components/dashboard/UpcomingFollowupsWidget';
import { FavoritesSection } from '../components/dashboard/FavoritesSection';
import { AnnouncementsPanel } from '../components/dashboard/AnnouncementsPanel';

const NAVY = '#0E1944';
const MAGENTA = '#E91E8C';
const COLORS = [NAVY, MAGENTA, '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4'];

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/analytics/executive'), api.get('/goals?period=monthly')]).then(([d, g]) => {
      setData(d.data);
      setGoals(g.data);
    });
  }, []);

  if (!data || !goals) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-36 rounded-2xl" />
        ))}
      </div>
    );
  }

  const pieData = Object.entries(data.status_distribution || {}).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k,
    value: v,
  }));

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      <AnnouncementsPanel />
      <FavoritesSection />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="Monthly Revenue"
          value={fmtCurrency(data.monthly_revenue)}
          rawValue={data.monthly_revenue}
          goal={goals.revenue}
          icon={DollarSign}
          testid="kpi-revenue"
        />
        <KpiCard
          label="Monthly Outreach"
          value={fmtNumber(data.monthly_outreach)}
          rawValue={data.monthly_outreach}
          goal={goals.outreach}
          icon={Megaphone}
          accent="navy"
          testid="kpi-outreach"
        />
        <KpiCard
          label="Active Pipeline"
          value={fmtCurrency(data.active_pipeline_value)}
          icon={Briefcase}
          testid="kpi-pipeline"
        />
        <KpiCard
          label="Media Partners Closed"
          value={data.media_partners_closed}
          goal={goals.media_partner_closures}
          icon={Award}
          accent="navy"
          testid="kpi-closed"
        />
        <KpiCard
          label="Conversion Rate"
          value={data.conversion_rate}
          suffix="%"
          icon={Percent}
          testid="kpi-conversion"
        />
        <KpiCard
          label="Upcoming Meetings"
          value={data.upcoming_meetings.length}
          icon={CalIcon}
          accent="navy"
          testid="kpi-meetings"
        />
        <KpiCard
          label="Follow-Ups Due"
          value={data.followups_due_count}
          icon={Bell}
          testid="kpi-followups"
        />
        <KpiCard
          label="Outstanding Tasks"
          value={data.open_tasks_count}
          icon={CheckSquare}
          accent="navy"
          testid="kpi-tasks"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">
                Revenue Trend
              </div>
              <h3 className="font-heading text-xl font-bold text-navy">Last 6 Months</h3>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.revenue_trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={MAGENTA} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={MAGENTA} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
              <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip
                formatter={(v) => fmtCurrency(v)}
                contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }}
              />
              <Area type="monotone" dataKey="revenue" stroke={MAGENTA} strokeWidth={3} fill="url(#revArea)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="mb-4">
            <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">
              Pipeline
            </div>
            <h3 className="font-heading text-xl font-bold text-navy">Lead Distribution</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="upcoming-meetings">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Upcoming Meetings</h3>
          <div className="space-y-3">
            {data.upcoming_meetings.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-8">No upcoming meetings</div>
            )}
            {data.upcoming_meetings.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0 last:pb-0"
              >
                <div>
                  <div className="font-semibold text-sm text-navy">{m.title}</div>
                  <div className="text-xs text-slate-500 capitalize">{m.event_type}</div>
                </div>
                <div className="text-xs text-slate-600 font-medium">{fmtDateTime(m.start_at)}</div>
              </div>
            ))}
          </div>
        </div>

        <UpcomingFollowupsWidget />

        <div className="bg-gradient-to-br from-navy to-navy/90 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-magenta">
                Monthly Goal
              </div>
              <div className="font-heading text-2xl font-bold mt-1">Revenue Target</div>
            </div>
            <DollarSign className="w-8 h-8 text-magenta" />
          </div>
          <div className="mt-6">
            <div className="text-4xl md:text-5xl font-heading font-bold">
              {fmtCurrency(data.monthly_revenue)}
            </div>
            <div className="text-sm text-white/60 mt-1">of {fmtCurrency(goals.revenue)} goal</div>
          </div>
          <div className="mt-6 h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-magenta rounded-full transition-all"
              style={{
                width: `${Math.min(100, (data.monthly_revenue / goals.revenue) * 100)}%`,
              }}
            />
          </div>
          <div className="mt-3 text-xs text-white/60">
            {goals?.revenue ? Math.round((data.monthly_revenue / goals.revenue) * 100) : 0}% achieved · {data.media_partners_closed_all_time} all-time partners closed
          </div>
        </div>
      </div>
    </div>
  );
}
